import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, ResponsiveContainer,
} from 'recharts';
import {
  Lightbulb, TrendUp, TrendDown, Minus, Trophy, CalendarCheck, AppWindow, Handshake, Medal, ChartBar,
  CaretRight,
} from '@phosphor-icons/react';
import { apiPath } from '../../config/apiBase';

/**
 * Three small Overview insights built from one endpoint
 * (/tickets/dashboard/resolutions-by-application):
 *   1. SolvedPerMonthWidget      — how many tickets were solved each month
 *   2. SolvedByApplicationWidget — per application, per month
 *   3. SolvedByPersonWidget      — per person, per month (optionally one application)
 * "Solved" = completed or closed (not cancelled), counted in the month it was
 * solved. Each person on a ticket is credited once for it.
 */

const API = apiPath('/api/tickets/dashboard/resolutions-by-application');
const CACHE_MS = 60 * 1000;
const ROW_PREVIEW = 6;

const RANGES = [3, 6, 12];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (key, withYear = false) => {
  const [y, m] = key.split('-').map(Number);
  return withYear ? `${MONTH_NAMES[m - 1]} ${y}` : `${MONTH_NAMES[m - 1]} ’${String(y).slice(2)}`;
};
const plural = (n, word, many) => `${n} ${n === 1 ? word : (many || `${word}s`)}`;
const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

// ------------------------------------------------------------ shared data --

// The three widgets ask for the same data; share one request per period.
const cache = {};
function fetchResolutions(months) {
  const hit = cache[months];
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.promise;
  const token = localStorage.getItem('token');
  const promise = axios
    .get(API, { withCredentials: true, headers: { Authorization: `Bearer ${token}` }, params: { months } })
    .then((r) => r.data)
    .catch((e) => { delete cache[months]; throw e; });
  cache[months] = { at: Date.now(), promise };
  return promise;
}

function useResolutions(months) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchResolutions(months)
      .then((data) => { if (!cancelled) setState({ data, loading: false, error: null }); })
      .catch((e) => {
        if (!cancelled) setState({ data: null, loading: false, error: e.response?.data?.detail || 'Could not load solved tickets.' });
      });
    return () => { cancelled = true; };
  }, [months]);
  return state;
}

/** Everyone across all applications: counts per month and totals. */
function peopleAcrossApps(data) {
  const map = {};
  data.applications.forEach((a) => a.people.forEach((p) => {
    const e = map[p.user_id] || (map[p.user_id] = {
      user_id: p.user_id, name: p.name, designation: p.designation, total: 0, apps: 0,
      counts: Object.fromEntries(data.months.map((k) => [k, 0])),
      appsByMonth: Object.fromEntries(data.months.map((k) => [k, 0])),
    });
    e.total += p.total;
    e.apps += 1;
    data.months.forEach((k) => {
      e.counts[k] += p.counts[k] || 0;
      if (p.counts[k]) e.appsByMonth[k] += 1;
    });
  }));
  return Object.values(map).sort((x, y) => y.total - x.total || (x.name || '').localeCompare(y.name || ''));
}

function trend(cur, prev) {
  if (prev == null) return null;
  if (prev === 0) return cur > 0 ? { icon: TrendUp, tone: '#059669', word: 'up from none' } : null;
  const p = Math.round(((cur - prev) / prev) * 100);
  if (p > 0) return { icon: TrendUp, tone: '#059669', word: `up ${p}%` };
  if (p < 0) return { icon: TrendDown, tone: '#DC2626', word: `down ${Math.abs(p)}%` };
  return { icon: Minus, tone: '#6B7280', word: 'no change' };
}

// -------------------------------------------------------------- UI pieces --

/**
 * Overview chart tiles are fixed squares (App.css: aspect-ratio 1/1) and the
 * card clips overflow, so the header stays put and everything below it
 * scrolls inside the tile with a visible scrollbar.
 */
/** Link to the full Tickets solved 360° page (same look as other cards' "Full view"). */
export function solved360Url({ view, months, app, month } = {}) {
  const sp = new URLSearchParams({ mode: 'solved' });
  if (view) sp.set('view', view);
  if (months) sp.set('months', String(months));
  if (app && app !== 'all') sp.set('app', app);
  if (month && month !== 'all') sp.set('month', month);
  return `/dashboard/tickets/pipeline?${sp.toString()}`;
}

function Card({ title, subtitle, months, setMonths, extra, children, testid, fullView }) {
  const navigate = useNavigate();
  return (
    <div className="wd-chart-card tk-surface tk-surface-pad is-auto-height" data-testid={testid}>
      <style>{SCROLLBAR_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', marginBottom: 8, flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <h3 className="wd-chart-title">{title}</h3>
          {subtitle && <p className="wd-chart-subtitle">{subtitle}</p>}
        </div>
        {fullView && (
          <button
            type="button"
            className="wd-perf-all-pipeline"
            onClick={() => navigate(fullView)}
            title="Open the complete detailed view"
            data-testid={`${testid}-360`}
          >
            360° view <CaretRight size={12} weight="bold" />
          </button>
        )}
        {extra}
        <div role="group" aria-label="Period" style={{ display: 'inline-flex', border: '1px solid #D1D5DB', borderRadius: 6, overflow: 'hidden', height: 26 }}>
          {RANGES.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setMonths(n)}
              title={`Last ${n} months`}
              style={{
                padding: '0 9px', fontSize: 11, border: 'none', cursor: 'pointer',
                background: months === n ? '#002FA7' : 'white',
                color: months === n ? 'white' : '#374151', fontWeight: months === n ? 600 : 500,
              }}
            >
              {n}M
            </button>
          ))}
        </div>
      </div>
      <div
        className="wd-solved-scroll"
        data-testid={`${testid}-scroll`}
        style={{
          flex: '1 1 auto', minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
          paddingRight: 6, scrollbarColor: '#94A3B8 #F1F5F9',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Status({ loading, error, empty, months }) {
  if (loading) return <p style={{ fontSize: 12, color: '#6B7280', padding: '20px 0' }}>Loading…</p>;
  if (error) return <p style={{ fontSize: 12, color: '#DC2626', padding: '20px 0' }}>{error}</p>;
  if (empty) return <p style={{ fontSize: 12, color: '#6B7280', padding: '20px 0' }}>No tickets were solved in the last {plural(months, 'month')}.</p>;
  return null;
}

function Insights({ items }) {
  if (!items.length) return null;
  return (
    <ul style={{
      listStyle: 'none', margin: '0 0 10px', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4,
      background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8,
    }}>
      {items.map((ins, i) => {
        const Icon = ins.icon || Lightbulb;
        return (
          <li key={i} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: 12, color: '#374151', lineHeight: 1.45 }}>
            <Icon size={14} weight="bold" color={ins.tone || '#F59E0B'} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{ins.text}</span>
          </li>
        );
      })}
    </ul>
  );
}

function cellShade(value, max) {
  if (!value) return { color: '#9CA3AF' };
  const steps = ['#EFF6FF', '#DBEAFE', '#BFDBFE', '#93C5FD', '#60A5FA'];
  const t = Math.min(1, value / Math.max(1, max));
  return { background: steps[Math.min(steps.length - 1, Math.floor(t * steps.length))], color: '#111827', fontWeight: t > 0.6 ? 700 : 500 };
}

const th = { padding: '6px 8px', fontSize: 11, fontWeight: 600, color: '#4B5563', textAlign: 'right', whiteSpace: 'nowrap', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' };
const td = { padding: '6px 8px', fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap', borderBottom: '1px solid #F3F4F6' };
const tf = { ...td, fontWeight: 700, background: '#F9FAFB', borderBottom: 'none', borderTop: '1px solid #E5E7EB' };
const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#002FA7', textAlign: 'left' };

/**
 * Rows × months of ticket counts. "Total tickets" sits right after the name so
 * it is always visible; months where nothing at all was solved are hidden
 * (and listed in a note) so the table only shows months with real numbers.
 */
const ROW_H = 34;      // fixed body row height in scrollable (listAll) tables
const HEAD_ROW = 28;   // height of the first header row (second row sticks below it)
const NUM_COL_W = 40;  // "#" column width; the name column is pinned right after it

// Visible, always-on scrollbar for the scrollable tables (WebKit; Firefox uses scrollbarColor).
const SCROLLBAR_CSS = `
.wd-solved-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.wd-solved-scroll::-webkit-scrollbar-track { background: #F1F5F9; border-radius: 8px; }
.wd-solved-scroll::-webkit-scrollbar-thumb { background: #94A3B8; border-radius: 8px; border: 2px solid #F1F5F9; }
.wd-solved-scroll::-webkit-scrollbar-thumb:hover { background: #64748B; }
.wd-hide-hscroll { scrollbar-width: none; }
.wd-hide-hscroll::-webkit-scrollbar { display: none; }
`;

/**
 * Wide table with a horizontal scrollbar that is always reachable: the
 * table's own (bottom-of-table) scrollbar is hidden and replaced by a synced
 * bar that sticks to the bottom of the card's visible area, so you can scroll
 * sideways from anywhere while scrolling up or down.
 */
function SyncedHScroll({ children }) {
  const bodyRef = useRef(null);
  const barRef = useRef(null);
  const syncing = useRef(false);
  const [size, setSize] = useState({ width: 0, overflow: false });

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    const update = () => setSize({
      width: body.scrollWidth,
      overflow: body.scrollWidth > body.clientWidth + 1,
    });
    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) {
      ro.observe(body);
      if (body.firstElementChild) ro.observe(body.firstElementChild);
    }
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [children]);

  const follow = (from, to) => {
    if (syncing.current) { syncing.current = false; return; }
    if (to.current && from.current && to.current.scrollLeft !== from.current.scrollLeft) {
      syncing.current = true;
      to.current.scrollLeft = from.current.scrollLeft;
    }
  };

  return (
    <div>
      <style>{SCROLLBAR_CSS}</style>
      <div
        ref={bodyRef}
        className="wd-hide-hscroll"
        onScroll={() => follow(bodyRef, barRef)}
        style={{ overflowX: 'auto', overflowY: 'hidden', border: '1px solid #E5E7EB', borderRadius: 8 }}
      >
        {children}
      </div>
      {size.overflow && (
        <div
          ref={barRef}
          className="wd-solved-scroll"
          onScroll={() => follow(barRef, bodyRef)}
          title="Scroll sideways to see more months"
          data-testid="solved-hscroll-bar"
          style={{
            position: 'sticky', bottom: 0, zIndex: 5, overflowX: 'auto', overflowY: 'hidden',
            height: 14, background: 'white', scrollbarColor: '#94A3B8 #F1F5F9',
          }}
        >
          <div style={{ width: size.width, height: 1 }} />
        </div>
      )}
    </div>
  );
}

/**
 * Rows × months of ticket counts. "Total tickets" sits right after the name so
 * it is always visible; months where nothing at all was solved are hidden
 * (and listed in a note) so the table only shows months with real numbers.
 *
 * listAll + maxHeight: every row is listed in a fixed-height box with its own
 * scrollbar, sticky header/totals rows, pinned # and name columns, zebra rows
 * and a hover highlight.
 */
function MonthTable({
  firstHeader, months: allMonths, rows, renderName, getCount, extraHeader, renderExtra, footer, testid,
  listAll = false, maxHeight = null,
}) {
  const [showAll, setShowAll] = useState(false);
  const [hoverKey, setHoverKey] = useState(null);

  const visible = listAll || showAll ? rows : rows.slice(0, ROW_PREVIEW);
  const months = allMonths.filter((k) => footer.value(k) > 0);
  const emptyMonths = allMonths.filter((k) => !(footer.value(k) > 0));
  const max = Math.max(1, ...rows.flatMap((r) => months.map((k) => getCount(r, k))));
  const scrollable = listAll && maxHeight != null;

  // Sticky positioning (only in the scrollable layout).
  const head1 = scrollable ? { position: 'sticky', top: 0, zIndex: 2, height: HEAD_ROW, boxSizing: 'border-box' } : {};
  const head2 = scrollable ? { position: 'sticky', top: HEAD_ROW, zIndex: 2 } : {};
  const foot = scrollable ? { position: 'sticky', bottom: 0, zIndex: 2 } : {};
  const pinNum = scrollable ? { position: 'sticky', left: 0, width: NUM_COL_W, minWidth: NUM_COL_W, boxSizing: 'border-box' } : {};
  const pinName = scrollable ? { position: 'sticky', left: NUM_COL_W, minWidth: 190, boxShadow: 'inset -1px 0 0 #E5E7EB' } : {};
  const corner = scrollable ? { zIndex: 4 } : {};

  const rowBg = (r, i) => {
    if (hoverKey === r.key) return '#E0EAFF';
    return i % 2 ? '#F8FAFC' : '#FFFFFF';
  };
  const bodyCell = scrollable ? { height: ROW_H, boxSizing: 'border-box' } : {};

  return (
    <>
      <SyncedHScroll>
        <table
          style={{ width: '100%', borderCollapse: scrollable ? 'separate' : 'collapse', borderSpacing: 0, background: 'white' }}
          data-testid={testid}
        >
          <thead>
            <tr>
              {listAll && <th rowSpan={2} style={{ ...th, ...head1, ...pinNum, ...corner, verticalAlign: 'bottom' }}>#</th>}
              <th rowSpan={2} style={{ ...th, ...head1, ...(listAll ? pinName : {}), ...corner, textAlign: 'left', verticalAlign: 'bottom' }}>{firstHeader}</th>
              <th rowSpan={2} style={{ ...th, ...head1, verticalAlign: 'bottom', color: '#111827' }}>Total tickets</th>
              {months.length > 0 && (
                <th colSpan={months.length} style={{ ...th, ...head1, textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                  Tickets solved in…
                </th>
              )}
              {extraHeader && <th rowSpan={2} style={{ ...th, ...head1, verticalAlign: 'bottom' }}>{extraHeader}</th>}
            </tr>
            <tr>
              {months.map((k) => <th key={k} style={{ ...th, ...head2, minWidth: 64 }}>{monthLabel(k)}</th>)}
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => {
              const bg = rowBg(r, i);
              return (
                <tr
                  key={r.key}
                  onMouseEnter={() => setHoverKey(r.key)}
                  onMouseLeave={() => setHoverKey((k) => (k === r.key ? null : k))}
                  style={{ background: bg }}
                >
                  {listAll && <td style={{ ...td, ...bodyCell, ...pinNum, zIndex: 1, background: bg, color: '#9CA3AF' }}>{r.rank ?? i + 1}</td>}
                  <td style={{ ...td, ...bodyCell, ...(listAll ? pinName : {}), zIndex: 1, background: bg, textAlign: 'left' }}>{renderName(r)}</td>
                  <td style={{ ...td, ...bodyCell, fontWeight: 700, color: '#111827' }}>{r.total}</td>
                  {months.map((k) => {
                    const v = getCount(r, k);
                    return (
                      <td
                        key={k}
                        title={`${r.name}: ${plural(v, 'ticket')} solved in ${monthLabel(k, true)}`}
                        style={{ ...td, ...bodyCell, ...(v ? cellShade(v, max) : { color: '#9CA3AF' }) }}
                      >
                        {v}
                      </td>
                    );
                  })}
                  {renderExtra && <td style={{ ...td, ...bodyCell }}>{renderExtra(r)}</td>}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              {listAll && <td style={{ ...tf, ...foot, ...pinNum, ...corner }} />}
              <td style={{ ...tf, ...foot, ...(listAll ? pinName : {}), ...corner, textAlign: 'left' }}>{footer.label}</td>
              <td style={{ ...tf, ...foot }}>{footer.total}</td>
              {months.map((k) => <td key={k} style={{ ...tf, ...foot }}>{footer.value(k)}</td>)}
              {extraHeader && <td style={{ ...tf, ...foot }}>{footer.extra ?? ''}</td>}
            </tr>
          </tfoot>
        </table>
      </SyncedHScroll>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#6B7280' }}>
          {emptyMonths.length > 0 && <>No tickets were solved in {emptyMonths.map((k) => monthLabel(k, true)).join(', ')}, so {emptyMonths.length === 1 ? 'it is' : 'they are'} not shown. </>}
          {scrollable && <>Hover a number to see the person and month. Darker = more tickets.</>}
        </span>
        {!listAll && rows.length > ROW_PREVIEW && (
          <button type="button" onClick={() => setShowAll((v) => !v)} style={linkBtn}>
            {showAll ? 'Show fewer' : `Show all ${rows.length}`}
          </button>
        )}
      </div>
    </>
  );
}

// ------------------------------------------- 1. Tickets solved per month --

export function SolvedPerMonthWidget() {
  const [months, setMonths] = useState(6);
  const { data, loading, error } = useResolutions(months);

  const view = useMemo(() => {
    if (!data) return null;
    const rows = data.months.map((k) => ({
      key: k, label: monthLabel(k), full: monthLabel(k, true),
      tickets: data.month_totals[k].tickets, people: data.month_totals[k].people,
    }));
    const total = rows.reduce((s, r) => s + r.tickets, 0);
    if (!total) return { rows, total, insights: [] };
    const cur = rows[rows.length - 1];
    const last = rows[rows.length - 2];
    const before = rows[rows.length - 3];
    const busiest = rows.reduce((b, r) => (r.tickets > b.tickets ? r : b), rows[0]);
    const insights = [];
    if (last) {
      const t = before ? trend(last.tickets, before.tickets) : null;
      insights.push({
        icon: t?.icon || CalendarCheck,
        tone: t?.tone,
        text: (
          <>
            <b>{last.full}:</b> {plural(last.tickets, 'ticket')} solved
            {t ? <> — {t.word} from {before.full} ({before.tickets})</> : null}.
            {' '}<b>{cur.full}</b> so far: {cur.tickets}.
          </>
        ),
      });
    }
    insights.push({
      icon: ChartBar,
      tone: '#7C3AED',
      text: (
        <>
          <b>Busiest month:</b> {busiest.full} with {plural(busiest.tickets, 'ticket')}. Average{' '}
          {Math.round(total / rows.length)} per month.
        </>
      ),
    });
    return { rows, total, insights };
  }, [data]);

  return (
    <Card
      testid="solved-per-month"
      fullView={solved360Url({ view: 'month', months })}
      title="Tickets solved per month"
      subtitle="Number of tickets completed or closed in each month."
      months={months}
      setMonths={setMonths}
    >
      <Status loading={loading} error={error} empty={view && !view.total} months={months} />
      {!loading && !error && view && view.total > 0 && (
        <>
          <Insights items={view.insights} />
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={view.rows} margin={{ top: 18, right: 4, left: -18, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#EEF0F3" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(59,130,246,0.06)' }}
                  formatter={(v, _n, p) => [`${v} tickets · ${p.payload.people} people`, 'Solved']}
                  labelFormatter={(_l, p) => p?.[0]?.payload?.full || _l}
                />
                <Bar dataKey="tickets" name="Tickets solved" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={40}>
                  <LabelList dataKey="tickets" position="top" style={{ fontSize: 11, fontWeight: 700, fill: '#111827' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Card>
  );
}

// ------------------------------------- 2. Tickets solved by application --

export function SolvedByApplicationWidget() {
  const [months, setMonths] = useState(6);
  const { data, loading, error } = useResolutions(months);

  const view = useMemo(() => {
    if (!data || !data.applications.length) return null;
    const apps = data.applications.map((a) => ({ ...a, key: a.key, total: a.tickets_total }));
    const all = apps.reduce((s, a) => s + a.total, 0);
    const top = apps[0];
    const multi = apps.reduce((s, a) => s + a.multi_person_total, 0);
    const insights = [
      {
        icon: AppWindow,
        tone: '#7C3AED',
        text: (
          <>
            <b>{top.name}</b> had the most: {plural(top.total, 'ticket')} ({pct(top.total, all)}% of all), solved by{' '}
            {plural(top.people_total, 'person', 'people')}.
          </>
        ),
      },
      {
        icon: Handshake,
        tone: '#0891B2',
        text: (
          <>
            <b>{plural(apps.length, 'application')}</b> had solved tickets; {plural(multi, 'ticket')} ({pct(multi, all)}%)
            needed two or more people.
          </>
        ),
      },
    ];
    return { apps, all, insights };
  }, [data]);

  return (
    <Card
      testid="solved-by-application"
      fullView={solved360Url({ view: 'application', months })}
      title="Tickets solved by application"
      subtitle="How many tickets each application had solved, month by month."
      months={months}
      setMonths={setMonths}
    >
      <Status loading={loading} error={error} empty={data && !view} months={months} />
      {!loading && !error && view && (
        <>
          <Insights items={view.insights} />
          <MonthTable
            testid="solved-by-application-table"
            firstHeader="Application"
            months={data.months}
            rows={view.apps}
            renderName={(a) => <span style={{ fontWeight: 600, color: '#111827' }}>{a.name}</span>}
            getCount={(a, k) => a.by_month[k].tickets}
            extraHeader="People"
            renderExtra={(a) => a.people_total}
            footer={{
              label: 'All applications',
              value: (k) => data.month_totals[k].tickets,
              total: view.all,
            }}
          />
        </>
      )}
    </Card>
  );
}

// ------------------------------------------- 3. Who solved the tickets --

function filterPeople(people, query) {
  const q = query.trim().toLowerCase();
  return q ? people.filter((p) => (p.name || '').toLowerCase().includes(q)) : people;
}

/** Month chips: "All months" + every month that has solved tickets. */
function MonthPicker({ months, value, onChange, count }) {
  const chip = (active) => ({
    height: 28, padding: '0 12px', fontSize: 12, borderRadius: 999, cursor: 'pointer',
    border: `1px solid ${active ? '#002FA7' : '#D1D5DB'}`,
    background: active ? '#002FA7' : 'white', color: active ? 'white' : '#374151', fontWeight: active ? 700 : 500,
  });
  return (
    <div role="group" aria-label="Month" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', marginRight: 2 }}>MONTH:</span>
      <button type="button" style={chip(value === 'all')} onClick={() => onChange('all')} data-testid="solved-by-person-month-all">
        All months
      </button>
      {months.map((k) => (
        <button
          key={k}
          type="button"
          style={chip(value === k)}
          onClick={() => onChange(k)}
          title={`${count(k)} tickets solved in ${monthLabel(k, true)}`}
          data-testid={`solved-by-person-month-${k}`}
        >
          {monthLabel(k, true)} <span style={{ opacity: 0.75 }}>({count(k)})</span>
        </button>
      ))}
    </div>
  );
}

/** One month: who solved tickets in it, most first (6 rows + Show all). */
function OneMonthTable({ month, people, monthTotal, showApps, onPerson, expanded = false }) {
  const [showAll, setShowAll] = useState(expanded);
  const rows = people.filter((p) => p.counts[month] > 0).sort((a, b) => b.counts[month] - a.counts[month]);
  const visible = showAll ? rows : rows.slice(0, ROW_PREVIEW);
  const top = rows[0] ? rows[0].counts[month] : 1;
  return (
    <>
      <SyncedHScroll>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }} data-testid="solved-by-person-month-table">
          <thead>
            <tr>
              <th style={{ ...th, width: 36 }}>#</th>
              <th style={{ ...th, textAlign: 'left' }}>Person</th>
              <th style={{ ...th, color: '#111827' }}>Tickets solved in {monthLabel(month, true)}</th>
              <th style={{ ...th, textAlign: 'left', width: '30%' }}>Compared with the top solver</th>
              <th style={th}>Share of month</th>
              {showApps && <th style={th}>Applications</th>}
            </tr>
          </thead>
          <tbody>
            {visible.map((p, i) => {
              const v = p.counts[month];
              return (
                <tr key={p.user_id} style={{ background: i % 2 ? '#F8FAFC' : 'white' }}>
                  <td style={{ ...td, color: '#9CA3AF' }}>{i + 1}</td>
                  <td style={{ ...td, textAlign: 'left' }}>
                    <button type="button" style={linkBtn} onClick={() => onPerson(p.user_id)} title="Open this person's solved tickets">
                      {p.name}
                    </button>
                  </td>
                  <td style={{ ...td, fontWeight: 700, color: '#111827' }}>{v}</td>
                  <td style={{ ...td, textAlign: 'left' }}>
                    <div style={{ height: 8, background: '#EFF6FF', borderRadius: 4, overflow: 'hidden' }} aria-hidden>
                      <div style={{ width: `${Math.max(2, (v / top) * 100)}%`, height: '100%', background: '#3B82F6', borderRadius: 4 }} />
                    </div>
                  </td>
                  <td style={td}>{pct(v, monthTotal)}%</td>
                  {showApps && <td style={td}>{p.appsByMonth?.[month] ?? '—'}</td>}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td style={tf} />
              <td style={{ ...tf, textAlign: 'left' }}>
                {plural(rows.length, 'person', 'people')} · tickets solved (each ticket once)
              </td>
              <td style={tf}>{monthTotal}</td>
              <td style={tf} />
              <td style={tf} />
              {showApps && <td style={tf} />}
            </tr>
          </tfoot>
        </table>
      </SyncedHScroll>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#6B7280' }}>
          Shared tickets count for each person, so the shares can add up to more than 100%.
        </span>
        {rows.length > ROW_PREVIEW && (
          <button type="button" onClick={() => setShowAll((s) => !s)} style={linkBtn} data-testid="solved-by-person-month-show-all">
            {showAll ? 'Show fewer' : `Show all ${rows.length}`}
          </button>
        )}
      </div>
    </>
  );
}

export function SolvedByPersonWidget() {
  const navigate = useNavigate();
  const [months, setMonths] = useState(6);
  const [appKey, setAppKey] = useState('all');
  const [month, setMonth] = useState('all');
  const [query, setQuery] = useState('');
  const { data, loading, error } = useResolutions(months);

  const app = data && appKey !== 'all' ? data.applications.find((a) => a.key === appKey) : null;

  const view = useMemo(() => {
    if (!data || !data.applications.length) return null;
    const people = (app ? app.people : peopleAcrossApps(data)).map((p, i) => ({ ...p, key: p.user_id, rank: i + 1 }));
    if (!people.length) return null;
    const ticketTotal = app ? app.tickets_total : data.applications.reduce((s, a) => s + a.tickets_total, 0);
    const monthTicket = (k) => (app ? app.by_month[k].tickets : data.month_totals[k].tickets);
    const activeMonths = data.months.filter((k) => monthTicket(k) > 0);
    const leader = people[0];
    const steady = [...people].sort((a, b) => {
      const am = data.months.filter((k) => a.counts[k]).length;
      const bm = data.months.filter((k) => b.counts[k]).length;
      return bm - am || b.total - a.total;
    })[0];
    const steadyMonths = data.months.filter((k) => steady.counts[k]).length;
    const insights = [
      {
        icon: Trophy,
        tone: '#B45309',
        text: (
          <>
            <b>{leader.name}</b> solved the most{app ? <> for {app.name}</> : null}: {plural(leader.total, 'ticket')}{' '}
            ({pct(leader.total, ticketTotal)}% of {app ? 'its' : 'all'} solved tickets).
          </>
        ),
      },
    ];
    if (data.months.length > 1 && steadyMonths > 1) {
      insights.push({
        icon: Medal,
        tone: '#0891B2',
        text: (
          <>
            <b>Most consistent:</b> {steady.name} solved tickets in {steadyMonths} of {data.months.length} months.{' '}
            {plural(people.length, 'person', 'people')} solved tickets in total.
          </>
        ),
      });
    }
    return { people, ticketTotal, monthTicket, activeMonths, insights };
  }, [data, app]);

  // Fall back to "All months" if the picked month isn't in the current period/application.
  const pickedMonth = view && month !== 'all' && view.activeMonths.includes(month) ? month : 'all';

  const monthInsights = useMemo(() => {
    if (!view || pickedMonth === 'all') return null;
    const ranked = view.people.filter((p) => p.counts[pickedMonth] > 0)
      .sort((a, b) => b.counts[pickedMonth] - a.counts[pickedMonth]);
    const total = view.monthTicket(pickedMonth);
    const top = ranked[0];
    const idx = view.activeMonths.indexOf(pickedMonth);
    const prev = idx > 0 ? view.activeMonths[idx - 1] : null;
    const t = prev ? trend(total, view.monthTicket(prev)) : null;
    const out = [{
      icon: t?.icon || CalendarCheck,
      tone: t?.tone || '#2563EB',
      text: (
        <>
          <b>{monthLabel(pickedMonth, true)}:</b> {plural(ranked.length, 'person', 'people')} solved{' '}
          {plural(total, 'ticket')}{app ? <> for {app.name}</> : null}
          {t ? <> — {t.word} from {monthLabel(prev, true)} ({view.monthTicket(prev)})</> : null}.
        </>
      ),
    }];
    if (top) {
      out.push({
        icon: Trophy,
        tone: '#B45309',
        text: (
          <>
            <b>{top.name}</b> solved the most that month: {plural(top.counts[pickedMonth], 'ticket')}{' '}
            ({pct(top.counts[pickedMonth], total)}% of the month).
          </>
        ),
      });
    }
    return out;
  }, [view, pickedMonth, app]);

  const openPerson = (userId) => {
    const sp = new URLSearchParams({ assigned_to: userId, view: 'closed' });
    if (app?.product_id) sp.set('product_id', app.product_id);
    navigate(`/dashboard/tickets/pipeline?${sp.toString()}`);
  };

  return (
    <Card
      testid="solved-by-person"
      fullView={solved360Url({ view: 'person', months, app: app ? appKey : 'all', month: pickedMonth })}
      title="Who solved the tickets"
      subtitle="How many tickets each person solved, month by month. Pick a month to see who solved tickets in it."
      months={months}
      setMonths={setMonths}
      extra={(
        <select
          value={app ? appKey : 'all'}
          onChange={(e) => setAppKey(e.target.value)}
          aria-label="Application"
          data-testid="solved-by-person-app"
          style={{ height: 26, padding: '0 6px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 6, background: 'white', maxWidth: 200 }}
        >
          <option value="all">All applications</option>
          {(data?.applications || []).map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
        </select>
      )}
    >
      <Status loading={loading} error={error} empty={data && !view} months={months} />
      {!loading && !error && view && (
        <>
          <Insights items={monthInsights || view.insights} />
          <MonthPicker months={view.activeMonths} value={pickedMonth} onChange={setMonth} count={view.monthTicket} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a person…"
              aria-label="Search a person"
              data-testid="solved-by-person-search"
              style={{ flex: '1 1 200px', maxWidth: 280, height: 28, padding: '0 8px', fontSize: 12, border: '1px solid #D1D5DB', borderRadius: 6 }}
            />
            {query.trim() && (
              <span style={{ fontSize: 11, color: '#6B7280' }}>
                {filterPeople(view.people, query).length} of {view.people.length} people match
              </span>
            )}
          </div>
          {pickedMonth === 'all' ? (
            <MonthTable
              testid="solved-by-person-table"
              firstHeader="Person"
              months={data.months}
              rows={filterPeople(view.people, query)}
              renderName={(p) => (
                <button type="button" style={linkBtn} onClick={() => openPerson(p.user_id)} title="Open this person's solved tickets">
                  {p.name}
                </button>
              )}
              getCount={(p, k) => p.counts[k] || 0}
              extraHeader={app ? null : 'Applications'}
              renderExtra={app ? null : (p) => p.apps}
              footer={{
                label: 'Tickets solved (each ticket once)',
                value: view.monthTicket,
                total: view.ticketTotal,
              }}
            />
          ) : (
            <OneMonthTable
              key={`${pickedMonth}:${appKey}:${query}`}
              month={pickedMonth}
              people={filterPeople(view.people, query)}
              monthTotal={view.monthTicket(pickedMonth)}
              showApps={!app}
              onPerson={openPerson}
            />
          )}
        </>
      )}
    </Card>
  );
}

// Shared with the full Tickets solved 360° page (modules/tickets/pages/Solved360Page.js).
export {
  fetchResolutions, peopleAcrossApps, filterPeople, monthLabel, plural, pct, trend,
  Insights, MonthPicker, MonthTable, OneMonthTable, SyncedHScroll, linkBtn, th, td, tf, cellShade,
};

export default SolvedPerMonthWidget;
